import JSZip from "jszip";
import * as THREE from "three";
import { modes, getModeByIndex, getDefaultValues, getDefaultLocks, toRenderSettings, type ModeDefinition, type RenderSettings } from "./modes";
import { renderModeControls, updateControlValues, type ControlState } from "./ui/controls";

// DOM elements
const viewport = document.getElementById("viewport") as HTMLCanvasElement;
const statusEl = document.getElementById("progressText") as HTMLDivElement;
const modeSelect = document.getElementById("modeSelect") as HTMLSelectElement;
const modeControlsContainer = document.getElementById("modeControls") as HTMLDivElement;
const exportResolutionSelect = document.getElementById("exportResolution") as HTMLSelectElement;
const progressFill = document.getElementById("progressFill") as HTMLDivElement;
const progressText = document.getElementById("progressText") as HTMLDivElement;

const randomizeButton = document.getElementById("randomize") as HTMLButtonElement;
const downloadButton = document.getElementById("download") as HTMLButtonElement;
const downloadCubeButton = document.getElementById("downloadCube") as HTMLButtonElement;
const downloadHdrButton = document.getElementById("downloadHdr") as HTMLButtonElement;
const hdrToggle = document.getElementById("hdrToggle") as HTMLInputElement;
const exposureInput = document.getElementById("exposure") as HTMLInputElement;
const ldrControls = document.getElementById("ldrControls") as HTMLDivElement;
const hdrControls = document.getElementById("hdrControls") as HTMLDivElement;
const seedInput = document.getElementById("seed") as HTMLInputElement;

interface RGB {
  r: number;
  g: number;
  b: number;
}

const TAU = Math.PI * 2;

// Three.js setup
const renderer = new THREE.WebGLRenderer({ canvas: viewport, antialias: true });
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.NoToneMapping;
renderer.toneMappingExposure = 1.0;

const scene = new THREE.Scene();
scene.background = new THREE.Color(0x05060c);
const camera = new THREE.PerspectiveCamera(65, window.innerWidth / window.innerHeight, 0.1, 50);

let currentTexture: THREE.Texture | null = null;

const orbit = {
  yaw: 0,
  pitch: 0,
  fov: 90,
  dragging: false,
  lastX: 0,
  lastY: 0,
};

// Current mode state
let currentMode: ModeDefinition = modes[0];
let controlState: ControlState = {
  values: getDefaultValues(currentMode),
  locks: getDefaultLocks(currentMode),
};

// Render state
interface TileJob {
  renderId: number;
  tileId: number;
  x: number;
  y: number;
  width: number;
  height: number;
  fullWidth: number;
  fullHeight: number;
  settings: RenderSettings;
}

interface RenderState {
  id: number;
  width: number;
  height: number;
  imageData: ImageData;
  hdrData: Uint16Array | null;
  hdrMode: boolean;
  totalTiles: number;
  tilesRemaining: number;
  tileProgress: Map<number, number>;
  label: string;
  resolve: (value: ImageData | Uint16Array) => void;
  reject: (reason?: Error) => void;
}

const workerCount = Math.max(1, Math.min(4, Math.max(1, (navigator.hardwareConcurrency ?? 4) - 1)));
const workers = Array.from({ length: workerCount }, () => new Worker(new URL("./worker.ts", import.meta.url), { type: "module" }));
const workerState = new Map<Worker, { busy: boolean }>();
let renderIdCounter = 0;
let tileIdCounter = 0;
let tileQueue: TileJob[] = [];
let activeRender: RenderState | null = null;
let lastRenderKey = "";
let lastImageData: ImageData | null = null;
let lastHdrData: Uint16Array | null = null;
let isHdrPreview = false;

workers.forEach((worker) => {
  workerState.set(worker, { busy: false });
});

// Float16 conversion
function float16ToFloat32(h: number): number {
  const sign = (h & 0x8000) >> 15;
  const exp = (h & 0x7c00) >> 10;
  const frac = h & 0x03ff;

  if (exp === 0) {
    if (frac === 0) return sign ? -0 : 0;
    let e = -14;
    let m = frac;
    while ((m & 0x400) === 0) {
      m <<= 1;
      e--;
    }
    m &= 0x3ff;
    return (sign ? -1 : 1) * Math.pow(2, e) * (1 + m / 1024);
  } else if (exp === 31) {
    return frac === 0 ? (sign ? -Infinity : Infinity) : NaN;
  }

  return (sign ? -1 : 1) * Math.pow(2, exp - 15) * (1 + frac / 1024);
}

function hdrDataToTexture(data: Uint16Array, width: number, height: number): THREE.DataTexture {
  const texture = new THREE.DataTexture(data, width, height, THREE.RGBAFormat, THREE.HalfFloatType);
  texture.mapping = THREE.EquirectangularReflectionMapping;
  texture.wrapS = THREE.RepeatWrapping;
  texture.repeat.x = -1;
  texture.flipY = true;
  texture.needsUpdate = true;
  return texture;
}

function resizeRenderer(): void {
  const width = window.innerWidth;
  const height = window.innerHeight;
  renderer.setSize(width, height);
  camera.aspect = width / height;
  camera.updateProjectionMatrix();
}

function updateCamera(): void {
  camera.rotation.order = "YXZ";
  camera.rotation.y = orbit.yaw;
  camera.rotation.x = orbit.pitch;
  camera.fov = orbit.fov;
  camera.updateProjectionMatrix();
}

function animate(): void {
  renderer.render(scene, camera);
  requestAnimationFrame(animate);
}

function clamp(value: number, min = 0, max = 1): number {
  return Math.min(max, Math.max(min, value));
}

function getSettings(): RenderSettings {
  const hdrMode = hdrToggle?.checked ?? false;
  const seed = seedInput?.value || 'default';
  return toRenderSettings(currentMode, controlState.values, seed, hdrMode, false);
}

function updateStatus(message: string): void {
  statusEl.textContent = message;
}

function updateDownloadState(): void {
  const busy = activeRender !== null;
  downloadButton.disabled = busy;
  downloadCubeButton.disabled = busy;
  if (downloadHdrButton) downloadHdrButton.disabled = busy;
}

function updateProgress(label: string, value: number): void {
  const percent = Math.round(value * 100);
  progressFill.style.width = `${percent}%`;
  progressText.textContent = `${label} ${percent}%`;
}

function resetProgress(text = "Idle"): void {
  progressFill.style.width = "0%";
  progressText.textContent = text;
}

function imageDataToTexture(imageData: ImageData): THREE.Texture {
  const canvas = document.createElement("canvas");
  canvas.width = imageData.width;
  canvas.height = imageData.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Texture canvas unavailable");
  }
  ctx.putImageData(imageData, 0, 0);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.mapping = THREE.EquirectangularReflectionMapping;
  texture.wrapS = THREE.RepeatWrapping;
  texture.repeat.x = -1;
  texture.needsUpdate = true;
  return texture;
}

function cancelActiveRender(reason: string): void {
  if (!activeRender) {
    return;
  }
  workers.forEach((worker) => {
    worker.postMessage({ type: "cancel", renderId: activeRender!.id });
    const state = workerState.get(worker);
    if (state) {
      state.busy = false;
    }
  });
  tileQueue = [];
  activeRender.reject(new Error(reason));
  activeRender = null;
  updateDownloadState();
}

function dispatchTiles(): void {
  for (const worker of workers) {
    const state = workerState.get(worker);
    if (!state || state.busy) {
      continue;
    }
    const nextTile = tileQueue.shift();
    if (!nextTile) {
      return;
    }
    state.busy = true;
    worker.postMessage({ type: "renderTile", ...nextTile });
  }
}

function renderSkyImageData(
  width: number,
  height: number,
  settings: RenderSettings,
  label: string,
): Promise<ImageData | Uint16Array> {
  return new Promise((resolve, reject) => {
    if (activeRender) {
      cancelActiveRender("Render cancelled");
    }
    const id = (renderIdCounter += 1);
    const imageData = new ImageData(width, height);
    const hdrData = settings.hdrMode ? new Uint16Array(width * height * 4) : null;
    const tileHeight = 128;
    const tiles: TileJob[] = [];

    for (let y = 0; y < height; y += tileHeight) {
      const h = Math.min(tileHeight, height - y);
      tiles.push({
        renderId: id,
        tileId: (tileIdCounter += 1),
        x: 0,
        y,
        width,
        height: h,
        fullWidth: width,
        fullHeight: height,
        settings,
      });
    }

    tileQueue = tiles.slice();
    activeRender = {
      id,
      width,
      height,
      imageData,
      hdrData,
      hdrMode: settings.hdrMode ?? false,
      totalTiles: tiles.length,
      tilesRemaining: tiles.length,
      tileProgress: new Map(),
      label,
      resolve,
      reject,
    };

    updateStatus(`${label} 0%`);
    updateProgress(label, 0);
    updateDownloadState();
    dispatchTiles();
  });
}

async function renderPreview(): Promise<void> {
  const settings = getSettings();
  const width = Number(exportResolutionSelect.value);
  const height = Math.floor(width / 2);
  const renderKey = JSON.stringify({ width, height, settings });

  // Configure tone mapping based on HDR mode
  if (settings.hdrMode) {
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = exposureInput ? Number(exposureInput.value) : 1.0;
  } else {
    renderer.toneMapping = THREE.NoToneMapping;
    renderer.toneMappingExposure = 1.0;
  }

  try {
    downloadButton.disabled = true;
    downloadCubeButton.disabled = true;
    if (downloadHdrButton) downloadHdrButton.disabled = true;
    const result = await renderSkyImageData(width, height, settings, "Rendering preview");

    let finalTexture: THREE.Texture;
    if (settings.hdrMode && result instanceof Uint16Array) {
      finalTexture = hdrDataToTexture(result, width, height);
      lastHdrData = result;
      lastImageData = null;
      isHdrPreview = true;
    } else {
      finalTexture = imageDataToTexture(result as ImageData);
      lastImageData = result as ImageData;
      lastHdrData = null;
      isHdrPreview = false;
    }

    if (currentTexture) {
      currentTexture.dispose();
    }
    currentTexture = finalTexture;
    scene.background = currentTexture;
    lastRenderKey = renderKey;

    const hdrLabel = settings.hdrMode ? " HDR" : "";
    updateStatus(`Preview ready: ${width} x ${height}${hdrLabel}`);
    resetProgress("Preview ready");
  } catch (error) {
    if (error instanceof Error && (error.message === "Render superseded" || error.message === "Render cancelled")) {
      return;
    }
    updateStatus("Preview failed.");
    resetProgress("Preview failed");
  } finally {
    updateDownloadState();
  }
}

function generateRandomSeed(): string {
  const syllables = ['orion', 'veil', 'nova', 'aurora', 'drift', 'rift', 'ember', 'sable', 'celest', 'echo', 'sunny', 'azure', 'breeze', 'dawn'];
  return `${syllables[Math.floor(Math.random() * syllables.length)]}-${Math.floor(Math.random() * 1000)}`;
}

function randomizeCurrentMode(): void {
  // Randomize seed
  const newSeed = generateRandomSeed();
  if (seedInput) {
    seedInput.value = newSeed;
  }

  // Randomize mode-specific params
  if (currentMode.randomize) {
    controlState.values = currentMode.randomize(controlState.values, controlState.locks);
    updateControlValues(modeControlsContainer, currentMode, controlState.values);
  }
}

async function downloadEquirectangular(): Promise<void> {
  if (activeRender) {
    return;
  }
  const settings = { ...getSettings(), hdrMode: false };
  const width = Number(exportResolutionSelect.value);
  const height = Math.floor(width / 2);
  const renderKey = JSON.stringify({ width, height, settings });

  try {
    downloadButton.disabled = true;
    downloadCubeButton.disabled = true;
    const imageData =
      lastRenderKey === renderKey && lastImageData
        ? lastImageData
        : (await renderSkyImageData(width, height, settings, `Rendering ${width} x ${height} export`)) as ImageData;
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      updateStatus("Export failed: canvas unavailable.");
      return;
    }
    ctx.putImageData(imageData, 0, 0);

    await downloadCanvas(canvas, `skybox-${settings.seed}-${width}.png`);
    updateStatus("Equirectangular export complete.");
    resetProgress("Export complete");
  } catch (error) {
    if (error instanceof Error && (error.message === "Render superseded" || error.message === "Render cancelled")) {
      return;
    }
    updateStatus("Export failed.");
    resetProgress("Export failed");
  } finally {
    updateDownloadState();
  }
}

function faceDirection(face: string, u: number, v: number): [number, number, number] {
  switch (face) {
    case "px": return [1, v, -u];
    case "nx": return [-1, v, u];
    case "py": return [u, 1, -v];
    case "ny": return [u, -1, v];
    case "pz": return [u, v, 1];
    case "nz":
    default: return [-u, v, -1];
  }
}

function sampleEquirect(data: Uint8ClampedArray, width: number, height: number, u: number, v: number): RGB {
  const x = Math.min(width - 1, Math.max(0, Math.floor(u * width)));
  const y = Math.min(height - 1, Math.max(0, Math.floor(v * height)));
  const idx = (y * width + x) * 4;
  return { r: data[idx], g: data[idx + 1], b: data[idx + 2] };
}

async function downloadCubemapFaces(): Promise<void> {
  if (activeRender) {
    return;
  }
  const settings = { ...getSettings(), sphericalMode: true, hdrMode: false };
  const width = Number(exportResolutionSelect.value);
  const height = Math.floor(width / 2);

  try {
    downloadButton.disabled = true;
    downloadCubeButton.disabled = true;
    const imageData = (await renderSkyImageData(width, height, settings, `Rendering ${width} x ${height} spherical map`)) as ImageData;
    const faces = ["px", "nx", "py", "ny", "pz", "nz"];
    const faceSize = Math.floor(height / 2);
    const zip = new JSZip();

    for (const face of faces) {
      updateStatus(`Rendering cubemap face ${face.toUpperCase()}...`);
      const canvas = document.createElement("canvas");
      canvas.width = faceSize;
      canvas.height = faceSize;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        updateStatus("Cubemap export failed: canvas unavailable.");
        return;
      }
      const faceImage = ctx.createImageData(faceSize, faceSize);
      const out = faceImage.data;
      let offset = 0;

      for (let y = 0; y < faceSize; y += 1) {
        const v = 1 - (2 * (y + 0.5)) / faceSize;
        for (let x = 0; x < faceSize; x += 1) {
          const u = (2 * (x + 0.5)) / faceSize - 1;
          let [dx, dy, dz] = faceDirection(face, u, v);
          const len = Math.hypot(dx, dy, dz) || 1;
          dx /= len;
          dy /= len;
          dz /= len;

          const theta = Math.atan2(dz, dx);
          const phi = Math.acos(clamp(dy, -1, 1));
          const eu = (theta + Math.PI) / TAU;
          const ev = phi / Math.PI;

          const color = sampleEquirect(imageData.data, width, height, eu, ev);
          out[offset] = color.r;
          out[offset + 1] = color.g;
          out[offset + 2] = color.b;
          out[offset + 3] = 255;
          offset += 4;
        }
      }

      ctx.putImageData(faceImage, 0, 0);
      const blob = await canvasToPngBlob(canvas);
      if (!blob) {
        updateStatus("Cubemap export failed: PNG encoding failed.");
        resetProgress("Cubemap failed");
        return;
      }
      zip.file(`skybox-${settings.seed}-${face}.png`, blob);
    }

    updateStatus("Packaging cubemap zip...");
    updateProgress("Packaging zip", 0.95);
    const zipBlob = await zip.generateAsync({ type: "blob", compression: "DEFLATE" });
    await downloadBlob(zipBlob, `skybox-${settings.seed}-cubemap.zip`);
    updateStatus("Cubemap export complete.");
    resetProgress("Cubemap complete");
  } catch (error) {
    if (error instanceof Error && (error.message === "Render superseded" || error.message === "Render cancelled")) {
      return;
    }
    updateStatus("Cubemap export failed.");
    resetProgress("Cubemap failed");
  } finally {
    updateDownloadState();
  }
}

async function downloadCanvas(canvas: HTMLCanvasElement, filename: string): Promise<void> {
  const blob = await canvasToPngBlob(canvas);
  if (!blob) {
    updateStatus("Export failed: PNG encoding failed.");
    return;
  }
  await downloadBlob(blob, filename);
}

async function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
}

async function downloadBlob(blob: Blob, filename: string): Promise<void> {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function encodeRGBE(rgba: Uint16Array, width: number, height: number): Uint8Array {
  const header = `#?RADIANCE\nFORMAT=32-bit_rle_rgbe\n\n-Y ${height} +X ${width}\n`;
  const headerBytes = new TextEncoder().encode(header);
  const pixelCount = width * height;
  const rgbeData = new Uint8Array(headerBytes.length + pixelCount * 4);

  rgbeData.set(headerBytes, 0);
  let offset = headerBytes.length;

  for (let i = 0; i < pixelCount; i++) {
    const r = Math.max(0, float16ToFloat32(rgba[i * 4]));
    const g = Math.max(0, float16ToFloat32(rgba[i * 4 + 1]));
    const b = Math.max(0, float16ToFloat32(rgba[i * 4 + 2]));

    const maxVal = Math.max(r, g, b);

    if (maxVal < 1e-32) {
      rgbeData[offset++] = 0;
      rgbeData[offset++] = 0;
      rgbeData[offset++] = 0;
      rgbeData[offset++] = 0;
    } else {
      let exp = Math.ceil(Math.log2(maxVal));
      const scale = Math.pow(2, -exp + 8);
      rgbeData[offset++] = Math.min(255, Math.max(0, Math.floor(r * scale)));
      rgbeData[offset++] = Math.min(255, Math.max(0, Math.floor(g * scale)));
      rgbeData[offset++] = Math.min(255, Math.max(0, Math.floor(b * scale)));
      rgbeData[offset++] = exp + 128;
    }
  }

  return rgbeData;
}

async function downloadHdr(): Promise<void> {
  if (activeRender) {
    return;
  }
  const settings = { ...getSettings(), hdrMode: true };
  const width = Number(exportResolutionSelect.value);
  const height = Math.floor(width / 2);

  try {
    downloadButton.disabled = true;
    downloadCubeButton.disabled = true;
    if (downloadHdrButton) downloadHdrButton.disabled = true;

    const result = await renderSkyImageData(width, height, settings, `Rendering ${width} x ${height} HDR`);

    if (!(result instanceof Uint16Array)) {
      updateStatus("HDR export failed: expected HDR data.");
      resetProgress("HDR export failed");
      return;
    }

    updateStatus("Encoding RGBE...");
    updateProgress("Encoding", 0.95);
    const rgbeData = encodeRGBE(result, width, height);
    const blob = new Blob([rgbeData], { type: "application/octet-stream" });
    await downloadBlob(blob, `skybox-${settings.seed}-${width}.hdr`);

    updateStatus("HDR export complete.");
    resetProgress("HDR export complete");
  } catch (error) {
    if (error instanceof Error && (error.message === "Render superseded" || error.message === "Render cancelled")) {
      return;
    }
    updateStatus("HDR export failed.");
    resetProgress("HDR export failed");
  } finally {
    updateDownloadState();
  }
}

function setupInteractions(): void {
  viewport.addEventListener("pointerdown", (event) => {
    orbit.dragging = true;
    orbit.lastX = event.clientX;
    orbit.lastY = event.clientY;
  });

  window.addEventListener("pointerup", () => {
    orbit.dragging = false;
  });

  window.addEventListener("pointermove", (event) => {
    if (!orbit.dragging) {
      return;
    }
    const dx = (event.clientX - orbit.lastX) / window.innerWidth;
    const dy = (event.clientY - orbit.lastY) / window.innerHeight;
    orbit.lastX = event.clientX;
    orbit.lastY = event.clientY;
    orbit.yaw -= dx * TAU * 0.25;
    orbit.pitch -= dy * Math.PI * 0.25;
    orbit.pitch = clamp(orbit.pitch, -1.2, 1.2);
    updateCamera();
  });

  viewport.addEventListener("wheel", (event) => {
    orbit.fov = clamp(orbit.fov + event.deltaY * 0.02, 35, 90);
    updateCamera();
  });
}

function initializeModeSelector(): void {
  // Populate mode selector
  modeSelect.innerHTML = modes.map(mode =>
    `<option value="${mode.modeIndex}">${mode.name}</option>`
  ).join('');

  modeSelect.addEventListener("change", () => {
    const modeIndex = Number(modeSelect.value);
    const newMode = getModeByIndex(modeIndex);
    if (newMode && newMode !== currentMode) {
      currentMode = newMode;
      controlState = {
        values: getDefaultValues(currentMode),
        locks: getDefaultLocks(currentMode),
      };
      renderCurrentModeControls();
      void renderPreview();
    }
  });
}

function renderCurrentModeControls(): void {
  renderModeControls(modeControlsContainer, currentMode, controlState, {
    onChange: (paramId, value) => {
      controlState.values[paramId] = value;
      void renderPreview();
    },
    onLockToggle: (_paramId, _locked) => {
      // Lock state is already updated in the controls module
    },
  });
}

function attachGlobalListeners(): void {
  randomizeButton.addEventListener("click", () => {
    randomizeCurrentMode();
    void renderPreview();
  });

  downloadButton.addEventListener("click", () => {
    void downloadEquirectangular();
  });

  downloadCubeButton.addEventListener("click", () => {
    void downloadCubemapFaces();
  });

  if (downloadHdrButton) {
    downloadHdrButton.addEventListener("click", () => {
      void downloadHdr();
    });
  }

  if (hdrToggle) {
    hdrToggle.addEventListener("change", () => {
      if (hdrToggle.checked) {
        ldrControls?.classList.add("hidden");
        hdrControls?.classList.remove("hidden");
      } else {
        ldrControls?.classList.remove("hidden");
        hdrControls?.classList.add("hidden");
      }
      void renderPreview();
    });
  }

  if (exposureInput) {
    exposureInput.addEventListener("input", () => {
      if (hdrToggle?.checked) {
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = Number(exposureInput.value);
      }
    });
  }

  exportResolutionSelect.addEventListener("change", () => {
    void renderPreview();
  });

  if (seedInput) {
    seedInput.addEventListener("change", () => {
      void renderPreview();
    });
  }
}

type WorkerMessage =
  | { type: "progress"; renderId: number; tileId: number; progress: number }
  | { type: "done"; renderId: number; tileId: number; x: number; y: number; width: number; height: number; buffer: ArrayBuffer; hdr?: boolean }
  | { type: "error"; renderId: number; tileId: number; error?: string };

function copyTile(imageData: ImageData, message: Extract<WorkerMessage, { type: "done" }>): void {
  const tileData = new Uint8ClampedArray(message.buffer);
  for (let row = 0; row < message.height; row += 1) {
    const srcOffset = row * message.width * 4;
    const destOffset = ((message.y + row) * imageData.width + message.x) * 4;
    imageData.data.set(tileData.subarray(srcOffset, srcOffset + message.width * 4), destOffset);
  }
}

function copyHdrTile(hdrData: Uint16Array, fullWidth: number, message: Extract<WorkerMessage, { type: "done" }>): void {
  const tileData = new Uint16Array(message.buffer);
  for (let row = 0; row < message.height; row += 1) {
    const srcOffset = row * message.width * 4;
    const destOffset = ((message.y + row) * fullWidth + message.x) * 4;
    hdrData.set(tileData.subarray(srcOffset, srcOffset + message.width * 4), destOffset);
  }
}

function updateRenderProgress(render: RenderState): void {
  let sum = 0;
  for (const value of render.tileProgress.values()) {
    sum += value;
  }
  const progress = render.totalTiles === 0 ? 0 : sum / render.totalTiles;
  updateStatus(`${render.label} ${Math.round(progress * 100)}%`);
  updateProgress(render.label, progress);
}

function handleWorkerMessage(worker: Worker, event: MessageEvent<WorkerMessage>): void {
  const message = event.data;

  if (message.type === "progress") {
    if (!activeRender || message.renderId !== activeRender.id) {
      return;
    }
    activeRender.tileProgress.set(message.tileId, message.progress);
    updateRenderProgress(activeRender);
    return;
  }

  if (message.type === "error") {
    const state = workerState.get(worker);
    if (state) {
      state.busy = false;
    }
    if (!activeRender || message.renderId !== activeRender.id) {
      dispatchTiles();
      return;
    }
    if (message.error === "Render cancelled") {
      dispatchTiles();
      updateDownloadState();
      return;
    }
    const reject = activeRender.reject;
    activeRender = null;
    updateDownloadState();
    reject(new Error(message.error ?? "Worker error"));
    dispatchTiles();
    return;
  }

  if (message.type === "done") {
    const state = workerState.get(worker);
    if (state) {
      state.busy = false;
    }
    if (!activeRender || message.renderId !== activeRender.id) {
      dispatchTiles();
      return;
    }

    if (message.hdr && activeRender.hdrData) {
      copyHdrTile(activeRender.hdrData, activeRender.width, message);
    } else {
      copyTile(activeRender.imageData, message);
    }

    activeRender.tileProgress.set(message.tileId, 1);
    activeRender.tilesRemaining -= 1;
    updateRenderProgress(activeRender);
    if (activeRender.tilesRemaining <= 0) {
      const resolve = activeRender.resolve;
      const finished = activeRender.hdrMode && activeRender.hdrData
        ? activeRender.hdrData
        : activeRender.imageData;
      activeRender = null;
      updateDownloadState();
      resolve(finished);
    }
    dispatchTiles();
  }
}

workers.forEach((worker) => {
  worker.addEventListener("message", (event) => handleWorkerMessage(worker, event));
});

window.addEventListener("resize", resizeRenderer);

// Initialize
setupInteractions();
initializeModeSelector();
renderCurrentModeControls();
attachGlobalListeners();
updateCamera();

// Sync HDR controls visibility
if (hdrToggle?.checked) {
  ldrControls?.classList.add("hidden");
  hdrControls?.classList.remove("hidden");
} else {
  ldrControls?.classList.remove("hidden");
  hdrControls?.classList.add("hidden");
}

// Initial render
randomizeCurrentMode();
void renderPreview();
animate();
